// ============================================================================
//  GoalModal.jsx — create or edit a SMART goal, with the Coach attached.
//
//  The Coach nudge dismissal is remembered in localStorage, which means the
//  Help page is the only place a man can rediscover the feature afterwards.
// ============================================================================

import React, { useState } from "react";
import { X, Save, Sparkles, Lock } from "lucide-react";
import {
  C, FS, display, kicker, overlay, sheet, lbl, inp, primaryBtn, iconBtn,
} from "../theme/tokens.js";
import CoachPanel from "./CoachPanel.jsx";

const NUDGE_STORAGE_KEY = "milemarker.coach_nudge_dismissed";

export default function GoalModal({ fKey, existing, onSave, onClose }) {
  const [f, setF] = useState(existing?.f ?? fKey ?? "faith");
  const [title, setTitle] = useState(existing?.title ?? "");
  const [sm, setSm] = useState({
    s: existing?.smart?.s ?? "", m: existing?.smart?.m ?? "", a: existing?.smart?.a ?? "",
    r: existing?.smart?.r ?? "", t: existing?.smart?.t ?? "",
  });
  const [privateDetails, setPrivateDetails] = useState(!!existing?.private_details);
  const [coachOpen, setCoachOpen] = useState(false);
  const [nudgeVisible, setNudgeVisible] = useState(() => {
    try { return !localStorage.getItem(NUDGE_STORAGE_KEY); }
    catch { return true; }
  });

  const dismissNudge = () => {
    setNudgeVisible(false);
    try { localStorage.setItem(NUDGE_STORAGE_KEY, "1"); } catch { /* private mode */ }
  };
  const openCoachViaNudge = () => {
    dismissNudge();
    setCoachOpen(true);
  };

  const rows = [
    ["s", "Specific", "What exactly will be accomplished?"],
    ["m", "Measurable", "How will you know it's done?"],
    ["a", "Attainable", "Realistic with diligent effort?"],
    ["r", "Relevant", "Why does it matter to your walk?"],
    ["t", "Time-bound", "By when? Cadence or deadline."],
  ];
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...sheet, maxWidth: 560 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ ...display, fontSize: 22, margin: 0, color: C.text }}>
            {existing ? "Edit Goal" : "New SMART Goal"}
          </h3>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        <div style={{ marginTop: 22 }}>
          <label style={lbl}>Which F?</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 9 }}>
            {FS.map((x) => (
              <button key={x.key} onClick={() => setF(x.key)} style={{
                padding: "8px 14px", borderRadius: 99, cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                letterSpacing: ".02em",
                border: f === x.key ? `1.5px solid ${C.red}` : `1.5px solid ${C.line}`,
                background: f === x.key ? C.redSoft : "transparent",
                color: f === x.key ? C.redDk : C.sub }}>{x.label}</button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={lbl}>Goal headline</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="A short name for this goal" style={inp} />
        </div>

        <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${C.line}` }}>
          <div style={kicker}>Make it S.M.A.R.T.</div>
          {rows.map(([k, name, hint]) => (
            <div key={k} style={{ marginTop: 14 }}>
              <label style={lbl}>{name} <span style={{ color: C.faint, fontWeight: 400 }}>· {hint}</span></label>
              <input value={sm[k]} onChange={(e) => setSm({ ...sm, [k]: e.target.value })} style={inp} />
            </div>
          ))}
        </div>

        {coachOpen ? (
          <CoachPanel goal={{ f, title, smart: sm }} onClose={() => setCoachOpen(false)} />
        ) : (
          <>
            {nudgeVisible && title.trim() && (
              <div style={{
                marginTop: 18, padding: "12px 14px", borderRadius: 12,
                background: "linear-gradient(135deg, rgba(213,0,50,0.07), rgba(213,0,50,0.02))",
                border: "1px solid rgba(213,0,50,0.22)",
                display: "flex", alignItems: "center", gap: 10,
                animation: "coach-nudge-in 260ms ease-out",
              }}>
                <div style={{
                  flexShrink: 0, width: 32, height: 32, borderRadius: 8,
                  background: "#fff", border: `1px solid rgba(213,0,50,0.20)`,
                  display: "grid", placeItems: "center",
                }}>
                  <Sparkles size={16} color={C.red} />
                </div>
                <div style={{ flex: 1, fontSize: 12.5, color: C.text, lineHeight: 1.45 }}>
                  <strong>Not sure this hits SMART?</strong>{" "}
                  <span style={{ color: C.sub }}>Coach can check it in a few seconds.</span>
                </div>
                <button type="button" onClick={openCoachViaNudge} style={{
                  padding: "7px 12px", borderRadius: 7, border: "none", background: C.red,
                  color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: ".02em",
                  cursor: "pointer", whiteSpace: "nowrap",
                }}>
                  Ask Coach
                </button>
                <button type="button" onClick={dismissNudge} title="Dismiss" style={{
                  padding: 4, border: "none", background: "transparent", cursor: "pointer",
                  color: C.faint, display: "grid", placeItems: "center",
                }}>
                  <X size={14} />
                </button>
              </div>
            )}
            <button type="button" onClick={() => setCoachOpen(true)}
              disabled={!title.trim()}
              style={{
                marginTop: nudgeVisible && title.trim() ? 10 : 18,
                width: "100%", padding: "11px 14px", borderRadius: 9,
                border: `1px dashed ${title.trim() ? C.red : C.line}`,
                background: title.trim() ? "rgba(213,0,50,0.04)" : "transparent",
                color: title.trim() ? C.redDk : C.faint,
                cursor: title.trim() ? "pointer" : "default",
                fontSize: 13, fontWeight: 700, letterSpacing: ".02em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
              <Sparkles size={14} />
              {title.trim() ? "Get Coach Feedback" : "Add a headline first to unlock the Coach"}
            </button>
          </>
        )}

        {/* Per-goal privacy. Hides the monthly write-ups from peers only; the
            goal itself stays on the roster so the group can still walk with him
            on it. Team Admins and above are unaffected. */}
        <label style={{ display: "flex", gap: 11, alignItems: "flex-start", marginTop: 20,
          padding: "13px 15px", borderRadius: 10, cursor: "pointer",
          background: privateDetails ? "rgba(213,0,50,0.05)" : C.shell,
          border: `1px solid ${privateDetails ? C.redLine : C.line}` }}>
          <input type="checkbox" checked={privateDetails}
            onChange={(e) => setPrivateDetails(e.target.checked)}
            style={{ marginTop: 2, width: 16, height: 16, accentColor: C.red, cursor: "pointer" }} />
          <span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5,
              fontWeight: 700, color: C.text }}>
              <Lock size={13} color={privateDetails ? C.red : C.sub} />
              Keep my updates private
            </span>
            <span style={{ display: "block", fontSize: 12.5, color: C.sub, lineHeight: 1.5,
              marginTop: 3 }}>
              Your monthly notes, blockers and photos on this goal stay between you
              and your Team Admin. Your teammates still see the goal, your
              percentage and your status.
            </span>
          </span>
        </label>

        <button onClick={() => {
            if (title.trim()) onSave({ f, title: title.trim(), smart: sm, private_details: privateDetails });
          }}
          style={{ ...primaryBtn, width: "100%", marginTop: 14 }}>
          <Save size={16} /> {existing ? "Save changes" : "Add goal"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PROFILE MODAL — name + optional photo (URL or upload)              */
/* ------------------------------------------------------------------ */
